import { HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AppApis } from "@app/core/APIs/app-apis";
import { CrudApiOptions, CrudApiService, GenResp } from "@template-ui/services/crud-api.service";
import { catchError, map, Observable, of } from "rxjs";
import {
  EzConfigurationResolutionResult,
  EzFieldMetadataCatalog,
  EzIssueCatalog,
  EzLookupCatalog,
  EzRuleBindingCatalog,
  EzTerminologyHealthReport,
  PlatformConfigurationDiagnosticResult,
  PlatformConfigurationDiagnosticScopeQuery,
  PlatformConfigurationFieldMetadataRequest,
  PlatformConfigurationLookupCatalogRequest,
  PlatformConfigurationRuleBindingsRequest,
  PlatformConfigurationTerminologyHealthRequest,
  toDiagnosticFailure,
  toDiagnosticSuccess,
} from "./platform-configuration-diagnostics.models";

const requestHeaders = new HttpHeaders({
  Accept: "application/json, text/plain, */*",
  "skip-error-interceptor": "yes",
  "skip-loading-interceptor": "yes",
});

export const platformConfigurationDiagnosticsApiOptions: Partial<CrudApiOptions<"body", "json">> = {
  retries: 0,
  feedbackOptions: {
    successMode: "none",
    errorMode: "none",
  },
  getOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
};

@Injectable({ providedIn: "root" })
export class PlatformConfigurationDiagnosticsService {
  private readonly apis = inject(AppApis);
  private readonly crudService = inject(CrudApiService);

  resolveConfiguration(
    request: PlatformConfigurationDiagnosticScopeQuery,
  ): Observable<PlatformConfigurationDiagnosticResult<EzConfigurationResolutionResult>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformConfigurationResolution(request.configurationCode, toScopeQuery(request)),
    );
  }

  getLookupCatalog(
    request: PlatformConfigurationLookupCatalogRequest,
  ): Observable<PlatformConfigurationDiagnosticResult<EzLookupCatalog>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformConfigurationLookupCatalog(
        request.configurationCode,
        request.domainCode,
        toScopeQuery(request),
      ),
    );
  }

  getFieldMetadataCatalog(
    request: PlatformConfigurationFieldMetadataRequest,
  ): Observable<PlatformConfigurationDiagnosticResult<EzFieldMetadataCatalog>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformConfigurationFieldMetadataCatalog(
        request.configurationCode,
        request.entityCode,
        toScopeQuery(request),
      ),
    );
  }

  getIssueCatalog(
    request: PlatformConfigurationDiagnosticScopeQuery,
  ): Observable<PlatformConfigurationDiagnosticResult<EzIssueCatalog>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformConfigurationIssueCatalog(request.configurationCode, toScopeQuery(request)),
    );
  }

  getRuleBindings(
    request: PlatformConfigurationRuleBindingsRequest,
  ): Observable<PlatformConfigurationDiagnosticResult<EzRuleBindingCatalog>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformConfigurationRuleBindings(request.configurationCode, {
        ...toScopeQuery(request),
        subjectCode: request.subjectCode,
        bindingTypeCode: request.bindingTypeCode,
      }),
    );
  }

  getTerminologyHealth(
    request: PlatformConfigurationTerminologyHealthRequest,
  ): Observable<PlatformConfigurationDiagnosticResult<EzTerminologyHealthReport>> {
    return this.getDiagnostic(
      this.apis.core.manual.getPlatformConfigurationTerminologyHealth(
        request.configurationCode,
        {
          ...toScopeQuery(request),
          localeCode: request.localeCode,
          channelCode: request.channelCode,
          audienceCode: request.audienceCode,
          tenantId: request.tenantId,
        },
      ),
    );
  }

  private getDiagnostic<T>(url: string): Observable<PlatformConfigurationDiagnosticResult<T>> {
    return this.crudService.getbyUrl<T>(url, platformConfigurationDiagnosticsApiOptions).pipe(
      map((response) => toDiagnosticSuccess(unwrapResponse(response))),
      catchError((error: unknown) => of(toDiagnosticFailure<T>(error))),
    );
  }
}

function toScopeQuery(request: PlatformConfigurationDiagnosticScopeQuery): Omit<
  PlatformConfigurationDiagnosticScopeQuery,
  "configurationCode"
> {
  return {
    scopeTypeCode: request.scopeTypeCode,
    scopeKey: request.scopeKey,
    asOfUtc: request.asOfUtc,
  };
}

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
  if (response && typeof response === "object" && "data" in response) {
    return (response as GenResp<T>).data;
  }

  return response as T;
}
